import { useRef, useState, useEffect } from 'react';
import { Check, ChevronDown, Monitor, Moon, Sun, Sunset } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

const OPTIONS = [
  { value: 'light', label: 'Light Mode', icon: Sun },
  { value: 'dark', label: 'Dark Mode', icon: Moon },
  { value: 'system', label: 'System Mode', icon: Monitor },
  { value: 'dim', label: 'Dim Mode', icon: Sunset },
];

/**
 * Navbar appearance control — single button that opens a themed dropdown.
 */
const ThemeToggle = () => {
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const activeOption = OPTIONS.find((o) => o.value === mode) || OPTIONS[2];
  const ActiveIcon = activeOption.icon;

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Appearance"
        title={`Appearance: ${activeOption.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 p-2 rounded-md border border-[var(--border-color)] bg-[var(--bg-muted)] text-[var(--icon-primary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] transition-colors duration-300"
      >
        <ActiveIcon className="w-5 h-5" aria-hidden />
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Appearance"
          className="absolute right-0 top-full mt-2 w-56 dropdown-panel z-50 py-1"
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-content-muted">
            Appearance
          </p>
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const selected = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setMode(opt.value);
                  setOpen(false);
                }}
                className={`dropdown-item flex items-center gap-2 ${
                  selected ? 'bg-[var(--bg-active)] text-[var(--brand-text)]' : ''
                }`}
              >
                <Icon
                  className={`w-4 h-4 shrink-0 ${selected ? 'text-[var(--brand-text)]' : ''}`}
                  aria-hidden
                />
                <span className="font-medium flex-1 text-left">{opt.label}</span>
                {selected && (
                  <Check className="w-4 h-4 shrink-0 text-[var(--brand-text)]" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
